import React, {useCallback, useEffect, useState} from "react";
import axios from "axios";
import {Alert, Card, CardContent, Typography,} from "@mui/material";
import {DataGrid, GridToolbar,} from "@mui/x-data-grid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {baseUrl} from "../Univ";
const VaccinationReports = () => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    /* ============================================================
       AUTH HEADERS
       ============================================================ */
    const headers = () => ({
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    });
    /* ============================================================
       DATE HANDLERS
       ============================================================ */
    const handleStartDateChange = (date) => {
        if (!date) {
            setStartDate(null);
            return;
        }
        const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
        setStartDate(localDate);
    };
    const handleEndDateChange = (date) => {
        if (!date) {
            setEndDate(null);
            return;
        }
        const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
        setEndDate(localDate);
    };
    /* ============================================================
       LOAD VACCINATION REPORT
       ============================================================ */
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError("");
        const startDateString = startDate ? startDate.toISOString().substring(0, 10) : "";
        const endDateString = endDate ? endDate.toISOString().substring(0, 10) : "";
        try {
            const response = await axios.get(`${baseUrl}vaccination/reports/`, {
                params: {
                    location: 1, from_date: startDateString || undefined, to_date: endDateString || undefined,
                }, headers: headers(),
            });
            console.log("Vaccination Report Response:", response.data);
            let data = [];
            if (Array.isArray(response.data)) {
                data = response.data;
            } else if (response.data && Array.isArray(response.data.results)) {
                data = response.data.results;
            } else if (response.data && Array.isArray(response.data.data)) {
                data = response.data.data;
            }
            /*
             * Convert nested FK objects into DataGrid-friendly
             * flat values.
             */
            const formattedData = data.map((item, index) => {
                const patient = item.patient && typeof item.patient === "object" ? item.patient : null;
                const vaccine = item.vaccine && typeof item.vaccine === "object" ? item.vaccine : null;
                const appointment = item.appointment && typeof item.appointment === "object" ? item.appointment : null;
                const schedule = item.schedule && typeof item.schedule === "object" ? item.schedule : null;
                const scheduleDose = item.schedule_dose && typeof item.schedule_dose === "object" ? item.schedule_dose : null;
                return {
                    /*
                     * DataGrid unique ID
                     */

                    id: item.id || index, /* ==================================================
                       ADMINISTRATION
                       ================================================== */

                    administration_id: item.id || "",
                    administered_date: item.administered_date || "",
                    administered_time: item.administered_time || "", /* ==================================================
                       PATIENT
                       ================================================== */

                    patient_db_id: patient?.id || (typeof item.patient !== "object" ? item.patient : ""),
                    patient_id: patient?.pat_id || "",
                    patient_name: patient?.patient || patient?.name || patient?.my_title || "",
                    patient_gender: patient?.gender || "",
                    patient_age: patient?.age || "",
                    patient_mobile: patient?.mobile || "", /* ==================================================
                       VACCINE
                       ================================================== */

                    vaccine_id: vaccine?.id || (typeof item.vaccine !== "object" ? item.vaccine : ""),
                    vaccine_name: vaccine?.vaccine_name || vaccine?.name || "",
                    vaccine_code: vaccine?.vaccine_code || "",
                    manufacturer: vaccine?.manufacturer || item.manufacturer || "", /* ==================================================
                       DOSE
                       ================================================== */

                    dose_number: item.dose_number || "",
                    dose_name: item.dose_name || "",
                    dose: item.dose || "", /* ==================================================
                       BATCH
                       ================================================== */

                    batch_number: item.batch_number || "", /* ==================================================
                       ADMINISTRATION DETAILS
                       ================================================== */

                    route: item.route || "",
                    vaccination_site: item.vaccination_site || "", /* ==================================================
                       APPOINTMENT
                       ================================================== */

                    appointment_id: appointment?.id || (typeof item.appointment !== "object" ? item.appointment : ""), /* ==================================================
                       SCHEDULE
                       ================================================== */

                    schedule_id: schedule?.id || (typeof item.schedule !== "object" ? item.schedule : ""),
                    schedule_name: schedule?.name || schedule?.title || schedule?.schedule_name || "", /* ==================================================
                       SCHEDULE DOSE
                       ================================================== */

                    schedule_dose_id: scheduleDose?.id || (typeof item.schedule_dose !== "object" ? item.schedule_dose : ""),
                    schedule_dose_number: scheduleDose?.dose_number || "",
                    schedule_dose_name: scheduleDose?.dose_name || "", /* ==================================================
                       STATUS
                       ================================================== */

                    status: item.status || "",
                    reminder_status: item.reminder_status || "",
                    due_date: item.due_date || "",
                    completed_date: item.completed_date || "",
                    overdue_days: item.overdue_days ?? 0, /* ==================================================
                       REMARKS
                       ================================================== */

                    remarks: item.remarks || "", /* ==================================================
                       AUDIT
                       ================================================== */

                    addedby: item.addedby || "",
                    add_date: item.add_date || "",
                    add_time: item.add_time || "",
                    ip_add: item.ip_add || "",
                    location: item.location || "",
                };
            });
            console.log("Formatted Vaccination Report:", formattedData);
            setRows(formattedData);
        } catch (err) {
            console.error("Vaccination report error:", err);
            console.error("Backend response:", err.response?.data);
            setRows([]);
            setError(err.response?.data?.detail || err.response?.data?.message || "Unable to load vaccination report.");
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate]);
    /* ============================================================
       INITIAL LOAD
       ============================================================ */
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    /* ============================================================
       DATAGRID COLUMNS
       ============================================================ */
    const columns = [{
        field: "administration_id", headerName: "Administration ID", width: 130,
    }, {
        field: "administered_date", headerName: "Date", width: 110,
    }, {
        field: "administered_time", headerName: "Time", width: 100,
    }, /* ========================================================
           PATIENT
           ======================================================== */

        {
            field: "patient_db_id", headerName: "Patient DB ID", width: 120,
        }, {
            field: "patient_id", headerName: "Patient ID", width: 110,
        }, {
            field: "patient_name", headerName: "Patient Name", width: 170,
        }, {
            field: "patient_gender", headerName: "Gender", width: 90,
        }, {
            field: "patient_age", headerName: "Age", width: 80,
        }, /* ========================================================
           VACCINE
           ======================================================== */

        {
            field: "vaccine_id", headerName: "Vaccine ID", width: 100,
        }, {
            field: "vaccine_code", headerName: "Vaccine Code", width: 130,
        }, {
            field: "vaccine_name", headerName: "Vaccine", width: 160,
        }, {
            field: "manufacturer", headerName: "Manufacturer", width: 160,
        }, /* ========================================================
           DOSE
           ======================================================== */

        {
            field: "dose_number", headerName: "Dose No", width: 90,
        }, {
            field: "dose_name", headerName: "Dose Name", width: 120,
        }, {
            field: "dose", headerName: "Dose", width: 100,
        }, /* ========================================================
           BATCH
           ======================================================== */

        {
            field: "batch_number", headerName: "Batch", width: 130,
        }, /* ========================================================
           ADMINISTRATION
           ======================================================== */

        {
            field: "route", headerName: "Route", width: 110,
        }, {
            field: "vaccination_site", headerName: "Site", width: 130,
        }, /* ========================================================
           FOREIGN KEYS
           ======================================================== */

        {
            field: "appointment_id", headerName: "Appointment ID", width: 130,
        }, {
            field: "schedule_id", headerName: "Schedule ID", width: 110,
        }, {
            field: "schedule_name", headerName: "Schedule", width: 160,
        }, {
            field: "schedule_dose_id", headerName: "Schedule Dose ID", width: 150,
        }, {
            field: "schedule_dose_number", headerName: "Schedule Dose No", width: 150,
        }, {
            field: "schedule_dose_name", headerName: "Schedule Dose", width: 150,
        }, /* ========================================================
           STATUS
           ======================================================== */

        {
            field: "status", headerName: "Status", width: 120,
        }, {
            field: "reminder_status", headerName: "Reminder", width: 120,
        }, {
            field: "due_date", headerName: "Due Date", width: 110,
        }, {
            field: "completed_date", headerName: "Completed Date", width: 130,
        }, {
            field: "overdue_days", headerName: "Overdue Days", width: 120,
        }, /* ========================================================
           REMARKS
           ======================================================== */

        {
            field: "remarks", headerName: "Remarks", width: 200,
        }, /* ========================================================
           AUDIT
           ======================================================== */

        {
            field: "addedby", headerName: "Added By", width: 120,
        }, {
            field: "add_date", headerName: "Add Date", width: 110,
        }, {
            field: "add_time", headerName: "Add Time", width: 110,
        }, {
            field: "ip_add", headerName: "IP Address", width: 140,
        }, {
            field: "location", headerName: "Location", width: 90,
        },];
    /* ============================================================
       SUMMARY
       ============================================================ */
    const totalRecords = rows.length;
    const completedRecords = rows.filter(row => String(row.status).toUpperCase() === "COMPLETED").length;
    const administeredRecords = rows.filter(row => String(row.status).toUpperCase() === "ADMINISTERED").length;
    /* ============================================================
       UI
       ============================================================ */
    return (<section className="container-fluid mt-4">

        {/* ====================================================
                HEADER
               ==================================================== */}

        <h3
            className="text-center"
            style={{
                color: "white", backgroundColor: "#0dcaf0", padding: "10px",
            }}
        >

            <i
                className="fa-solid fa-syringe"
                aria-hidden="true"
                style={{
                    marginRight: "10px",
                }}
            />

            Vaccination Reports

        </h3>


        {/* ====================================================
                ERROR
               ==================================================== */}

        {error && (<Alert
            severity="error"
            sx={{mb: 2}}
        >
            {error}
        </Alert>)}


        {/* ====================================================
                DATE FILTER
               ==================================================== */}

        <Card sx={{mb: 3}}>

            <CardContent>

                <div className="row">

                    <div className="col-md-6">

                        <label className="form-label">
                            Select Date
                        </label>

                        <div
                            className="date-filter-container"
                        >

                            <DatePicker
                                selected={startDate}
                                onChange={handleStartDateChange}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                placeholderText="From Date"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                className="date-picker-input"
                            />

                            <DatePicker
                                selected={endDate}
                                onChange={handleEndDateChange}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                placeholderText="To Date"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                className="date-picker-input"
                            />

                        </div>

                    </div>

                </div>

            </CardContent>

        </Card>


        {/* ====================================================
                SUMMARY
               ==================================================== */}

        <div
            className="row mb-3"
        >

            <div className="col-md-4">

                <Card>

                    <CardContent>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Total Records
                        </Typography>

                        <Typography
                            variant="h5"
                            fontWeight={700}
                        >
                            {totalRecords}
                        </Typography>

                    </CardContent>

                </Card>

            </div>


            <div className="col-md-4">

                <Card>

                    <CardContent>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Administered
                        </Typography>

                        <Typography
                            variant="h5"
                            fontWeight={700}
                        >
                            {administeredRecords}
                        </Typography>

                    </CardContent>

                </Card>

            </div>


            <div className="col-md-4">

                <Card>

                    <CardContent>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Completed
                        </Typography>

                        <Typography
                            variant="h5"
                            fontWeight={700}
                        >
                            {completedRecords}
                        </Typography>

                    </CardContent>

                </Card>

            </div>

        </div>


        {/* ====================================================
                DATAGRID
               ==================================================== */}

        <div
            style={{
                width: "100%",
            }}
        >

            <DataGrid

                rows={rows}

                columns={columns}

                getRowId={(row) => row.id}

                loading={isLoading}

                autoHeight

                pageSizeOptions={[25, 50, 100, 500, 1000,]}

                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 25, page: 0,
                        },
                    },
                }}

                slots={{
                    toolbar: GridToolbar,
                }}

                sx={{
                    boxShadow: 4, border: 4, borderColor: "primary.light", "& .MuiDataGrid-cell:hover": {
                        color: "primary.main",
                    }, "& .MuiDataGrid-columnHeaders": {
                        fontWeight: 700,
                    },
                }}

            />

        </div>

    </section>);
};
export default VaccinationReports;
